(() => {
  document.documentElement.classList.remove('no-js');

  const strings = window.PlayTimeThemeStrings || {};
  const themeString = (key, fallback, replacements = {}) => {
    const template = strings[key] || fallback;
    return Object.entries(replacements).reduce((value, [token, replacement]) => value.replaceAll(token, replacement), template);
  };

  const ensureProductVendorTopline = async (root = document) => {
    const sections = root.querySelectorAll?.('[data-playtime-product]') || [];

    sections.forEach(async (section) => {
      const summary = section.querySelector('.playtime-product__summary');
      const title = summary?.querySelector('h1');
      if (!summary || !title || summary.querySelector('.playtime-product__topline')) return;

      const oldEyebrow = summary.querySelector('.playtime-product__eyebrow');
      const statusRow = summary.querySelector('.ptb-row');
      const topLine = document.createElement('div');
      const vendorBadge = document.createElement('a');

      topLine.className = 'playtime-product__topline';
      vendorBadge.className = 'playtime-product__vendor-badge';
      vendorBadge.hidden = true;
      topLine.append(vendorBadge);

      if (statusRow) topLine.append(statusRow);
      title.before(topLine);
      oldEyebrow?.remove();

      try {
        const response = await fetch(`${window.location.pathname}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Product vendor unavailable');
        const productData = await response.json();
        const vendor = productData.vendor || '';
        if (!vendor) {
          vendorBadge.remove();
          return;
        }
        vendorBadge.textContent = vendor;
        vendorBadge.href = `/collections/vendors?q=${encodeURIComponent(vendor)}`;
        vendorBadge.hidden = false;
      } catch (error) {
        vendorBadge.remove();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ensureProductVendorTopline());
  } else {
    ensureProductVendorTopline();
  }
  document.addEventListener('shopify:section:load', (event) => ensureProductVendorTopline(event.target));

  const header = document.querySelector('[data-pt-header]');
  const menu = document.querySelector('[data-pt-menu]');
  const openButton = document.querySelector('[data-pt-menu-open]');
  const closeButton = document.querySelector('[data-pt-menu-close]');
  const menuOverlay = document.querySelector('[data-pt-menu-overlay]');
  const mobileMenuQuery = window.matchMedia('(max-width: 989px)');
  let menuOpen = false;
  let previousFocus = null;
  let lockedScrollY = 0;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const lockPageScroll = () => {
    lockedScrollY = window.scrollY;
    document.documentElement.classList.add('pt-menu-open');
    document.body.classList.add('pt-menu-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.width = '100%';
  };

  const unlockPageScroll = () => {
    document.documentElement.classList.remove('pt-menu-open');
    document.body.classList.remove('pt-menu-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, lockedScrollY);
  };

  const getMenuFocusables = () => [...(menu?.querySelectorAll(focusableSelector) || [])].filter((element) => !element.hasAttribute('inert'));

  const updateMenuAccessibility = () => {
    if (!menu || !openButton) return;
    const shouldHideMenu = mobileMenuQuery.matches && !menuOpen;

    openButton.setAttribute('aria-expanded', String(menuOpen));
    menu.setAttribute('aria-hidden', String(shouldHideMenu));
    menu.inert = shouldHideMenu;
  };

  const setMenu = (open) => {
    if (!menu || !openButton) return;
    if (open === menuOpen) return;

    menuOpen = open;
    menu.classList.toggle('is-open', open);
    menuOverlay?.classList.toggle('is-visible', open);

    if (open) {
      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : openButton;
      lockPageScroll();
      updateMenuAccessibility();
      window.requestAnimationFrame(() => {
        const firstTarget = closeButton || getMenuFocusables()[0];
        firstTarget?.focus({ preventScroll: true });
      });
    } else {
      unlockPageScroll();
      updateMenuAccessibility();
      if (previousFocus instanceof HTMLElement && document.contains(previousFocus)) {
        previousFocus.focus({ preventScroll: true });
      }
      previousFocus = null;
    }
  };

  openButton?.addEventListener('click', () => setMenu(true));
  closeButton?.addEventListener('click', () => setMenu(false));
  menuOverlay?.addEventListener('click', () => setMenu(false));

  menu?.addEventListener('click', (event) => {
    if (event.target instanceof HTMLAnchorElement) setMenu(false);
  });

  document.addEventListener('click', (event) => {
    if (!menuOpen || !mobileMenuQuery.matches || !menu || !openButton) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (!menu.contains(target) && !openButton.contains(target)) setMenu(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setMenu(false);
      return;
    }

    if (event.key !== 'Tab' || !menuOpen || !mobileMenuQuery.matches) return;

    const focusables = getMenuFocusables();
    if (!focusables.length) {
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  const handleMenuBreakpointChange = () => {
    if (!mobileMenuQuery.matches && menuOpen) setMenu(false);
    updateMenuAccessibility();
  };

  if (mobileMenuQuery.addEventListener) {
    mobileMenuQuery.addEventListener('change', handleMenuBreakpointChange);
  } else {
    mobileMenuQuery.addListener(handleMenuBreakpointChange);
  }

  updateMenuAccessibility();

  const updateHeader = () => {
    header?.classList.toggle('is-scrolled', window.scrollY > 8);
  };

  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });

  const footerMenuQuery = window.matchMedia('(max-width: 749px)');
  const footerMenus = document.querySelectorAll('[data-pt-footer-menu]');

  const setFooterMenu = (menuItem, open) => {
    const toggle = menuItem.querySelector('[data-pt-footer-toggle]');
    const panel = menuItem.querySelector('[data-pt-footer-panel]');
    menuItem.classList.toggle('is-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    toggle?.setAttribute('tabindex', footerMenuQuery.matches ? '0' : '-1');
    panel?.setAttribute('aria-hidden', String(footerMenuQuery.matches && !open));
    if (panel) panel.hidden = footerMenuQuery.matches && !open;
  };

  const syncFooterMenus = () => {
    footerMenus.forEach((menuItem) => {
      setFooterMenu(menuItem, !footerMenuQuery.matches);
    });
  };

  footerMenus.forEach((menuItem) => {
    const toggle = menuItem.querySelector('[data-pt-footer-toggle]');
    toggle?.addEventListener('click', () => {
      if (!footerMenuQuery.matches) return;
      setFooterMenu(menuItem, !menuItem.classList.contains('is-open'));
    });
  });

  if (footerMenuQuery.addEventListener) {
    footerMenuQuery.addEventListener('change', syncFooterMenus);
  } else {
    footerMenuQuery.addListener(syncFooterMenus);
  }

  syncFooterMenus();

  document.querySelectorAll('[data-pt-carousel-prev], [data-pt-carousel-next]').forEach((button) => {
    button.addEventListener('click', () => {
      const section = button.closest('section');
      const carousel = section?.querySelector('[data-pt-carousel]');
      if (!carousel) return;

      const direction = button.hasAttribute('data-pt-carousel-next') ? 1 : -1;
      const distance = Math.max(280, Math.round(carousel.clientWidth * 0.86));
      carousel.scrollBy({ left: direction * distance, behavior: 'smooth' });
    });
  });

  const updatePlayTimeCarouselControls = (carousel) => {
    const section = carousel.closest('section');
    if (!section) return;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth - 2;
    const hasOverflow = maxScroll > 2;
    const atStart = carousel.scrollLeft <= 2;
    const atEnd = carousel.scrollLeft >= maxScroll;
    section.classList.toggle('has-carousel-overflow', hasOverflow);

    section.querySelectorAll('[data-playtime-carousel-prev]').forEach((button) => {
      button.disabled = !hasOverflow || atStart;
      button.setAttribute('aria-disabled', String(!hasOverflow || atStart));
    });
    section.querySelectorAll('[data-playtime-carousel-next]').forEach((button) => {
      button.disabled = !hasOverflow || atEnd;
      button.setAttribute('aria-disabled', String(!hasOverflow || atEnd));
    });
  };

  document.querySelectorAll('[data-playtime-carousel]').forEach((carousel) => {
    updatePlayTimeCarouselControls(carousel);
    carousel.addEventListener('scroll', () => updatePlayTimeCarouselControls(carousel), { passive: true });
    window.addEventListener('resize', () => updatePlayTimeCarouselControls(carousel), { passive: true });
  });

  document.querySelectorAll('[data-playtime-carousel-prev], [data-playtime-carousel-next]').forEach((button) => {
    button.addEventListener('click', () => {
      const section = button.closest('section');
      const carousel = section?.querySelector('[data-playtime-carousel]');
      if (!carousel) return;

      const direction = button.hasAttribute('data-playtime-carousel-next') ? 1 : -1;
      const firstCard = carousel.querySelector(':scope > *');
      const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 280;
      const gap = parseFloat(getComputedStyle(carousel).columnGap || getComputedStyle(carousel).gap || 16);
      carousel.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
      window.setTimeout(() => updatePlayTimeCarouselControls(carousel), 320);
    });
  });

  const getCartBadges = () => document.querySelectorAll('.pt-icon-button--cart span, .cart-badge');

  const animateCartBadges = () => {
    getCartBadges().forEach((badge) => {
      badge.classList.remove('is-updated');
      void badge.offsetWidth;
      badge.classList.add('is-updated');
      window.setTimeout(() => badge.classList.remove('is-updated'), 700);
    });
  };

  const getGlobalCartToast = () => {
    let toast = document.querySelector('[data-global-cart-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'playtime-cart-toast';
    toast.setAttribute('role', 'dialog');
    toast.setAttribute('aria-modal', 'false');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    toast.setAttribute('aria-hidden', 'true');
    toast.setAttribute('data-global-cart-toast', '');
    document.body.append(toast);

    toast.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[data-cart-toast-close], .playtime-cart-toast__overlay')) {
        window.clearTimeout(toast._ptHideTimer);
        toast.classList.remove('is-visible');
        toast.setAttribute('aria-hidden', 'true');
      }
    });
    return toast;
  };

  const renderCartToast = (target, { title, viewCartUrl = '/cart', continueUrl = '/collections/all', error = false }) => {
    if (target && target !== document.querySelector('[data-global-cart-toast]')) {
      target.classList.remove('is-visible');
      target.classList.toggle('is-error', error);
      target.textContent = title;
    }

    const toast = getGlobalCartToast();
    toast.classList.add('is-visible');
    toast.classList.toggle('is-error', error);
    toast.setAttribute('aria-hidden', 'false');
    toast.replaceChildren();

    const overlay = document.createElement('button');
    overlay.className = 'playtime-cart-toast__overlay';
    overlay.type = 'button';
    overlay.setAttribute('aria-label', themeString('continueShopping', 'Continue shopping'));
    toast.append(overlay);

    const panel = document.createElement('div');
    panel.className = 'playtime-cart-toast__panel';

    const close = document.createElement('button');
    close.className = 'playtime-cart-toast__close';
    close.type = 'button';
    close.setAttribute('data-cart-toast-close', '');
    close.setAttribute('aria-label', themeString('continueShopping', 'Continue shopping'));
    close.innerHTML = '&times;';
    panel.append(close);

    const mark = document.createElement('span');
    mark.className = 'playtime-cart-toast__mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = error ? '!' : 'OK';
    panel.append(mark);

    const message = document.createElement('strong');
    message.textContent = title;
    panel.append(message);

    const text = document.createElement('p');
    text.textContent = error
      ? themeString('unableAdd', 'Unable to add product to cart.')
      : themeString('cartModalText', 'Your selection has been saved. Checkout remains secure and discreet.');
    panel.append(text);

    if (!error) {
      const actions = document.createElement('span');
      actions.className = 'playtime-cart-toast__actions';
      const viewCart = document.createElement('a');
      viewCart.href = viewCartUrl;
      viewCart.textContent = themeString('viewCart', 'View cart');
      const continueShopping = document.createElement('button');
      continueShopping.type = 'button';
      continueShopping.setAttribute('data-cart-toast-close', '');
      continueShopping.textContent = themeString('continueShopping', 'Continue shopping');
      actions.append(viewCart, continueShopping);
      panel.append(actions);
    }

    toast.append(panel);

    window.clearTimeout(toast._ptHideTimer);
    toast._ptHideTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, error ? 5200 : 4200);
  };

  const updateCartCount = async (animate = false) => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      getCartBadges().forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.setAttribute('aria-label', themeString('itemsInCart', `${cart.item_count} items in cart`, { __COUNT__: String(cart.item_count) }));
      });
      if (animate) animateCartBadges();
      return cart;
    } catch (error) {
      console.warn('Cart count update failed', error);
      return null;
    }
  };

  const formatCartMoney = (cents) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || '?' });
  };

  document.querySelectorAll('[data-main-cart-form]').forEach((cartForm) => {
    const subtotal = cartForm.querySelector('[data-cart-subtotal]');
    const status = cartForm.querySelector('[data-cart-status]');
    const emptyState = document.querySelector('[data-cart-empty-state]');
    const quantityTimers = new WeakMap();

    const setCartStatus = (message) => {
      if (!status) return;
      status.textContent = message;
    };

    const syncCartRows = (cart) => {
      const cartItems = new Map(cart.items.map((item, index) => [item.key, { ...item, line: index + 1 }]));

      cartForm.querySelectorAll('[data-cart-item]').forEach((row) => {
        const item = cartItems.get(row.getAttribute('data-cart-key'));

        if (!item) {
          row.classList.add('is-removing');
          window.setTimeout(() => row.remove(), 220);
          return;
        }

        row.setAttribute('data-cart-line', String(item.line));
        row.querySelector('[data-cart-line-price]').textContent = formatCartMoney(item.final_line_price);
        const input = row.querySelector('[data-cart-quantity-input]');
        if (input) {
          input.value = item.quantity;
          input.setAttribute('data-cart-current', String(item.quantity));
        }
      });

      if (subtotal) subtotal.textContent = formatCartMoney(cart.total_price);

      getCartBadges().forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.setAttribute('aria-label', themeString('itemsInCart', `${cart.item_count} items in cart`, { __COUNT__: String(cart.item_count) }));
      });
      animateCartBadges();

      if (cart.item_count === 0) {
        cartForm.hidden = true;
        emptyState?.removeAttribute('hidden');
      }
    };

    const updateCartLine = async (row, quantity) => {
      if (!row || row.classList.contains('is-loading')) return;

      const input = row.querySelector('[data-cart-quantity-input]');
      const previousQuantity = Number(input?.getAttribute('data-cart-current') || input?.value || 0);
      const line = Number(row.getAttribute('data-cart-line'));
      const nextQuantity = Math.max(0, Number.isFinite(quantity) ? quantity : previousQuantity);

      if (!line || nextQuantity === previousQuantity) return;

      row.classList.add('is-loading');
      row.toggleAttribute('aria-busy', true);
      row.querySelectorAll('button, input').forEach((control) => {
        control.disabled = true;
      });
      if (input) input.value = nextQuantity;
      setCartStatus(nextQuantity === 0 ? themeString('removingItem', 'Removing item...') : themeString('updatingQuantity', 'Updating quantity...'));

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ line, quantity: nextQuantity })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || themeString('unableUpdate', 'Unable to update your cart.'));
        }

        const cart = await response.json();
        syncCartRows(cart);
        setCartStatus(nextQuantity === 0 ? themeString('itemRemoved', 'Item removed from cart.') : themeString('cartUpdated', 'Cart updated.'));
      } catch (error) {
        if (input) input.value = previousQuantity;
        setCartStatus(error.message);
      } finally {
        row.classList.remove('is-loading');
        row.toggleAttribute('aria-busy', false);
        row.querySelectorAll('button, input').forEach((control) => {
          control.disabled = false;
        });
      }
    };

    cartForm.querySelectorAll('[data-cart-item]').forEach((row) => {
      const input = row.querySelector('[data-cart-quantity-input]');

      row.querySelector('[data-cart-quantity-minus]')?.addEventListener('click', () => {
        updateCartLine(row, Math.max(0, Number(input?.value || 0) - 1));
      });

      row.querySelector('[data-cart-quantity-plus]')?.addEventListener('click', () => {
        updateCartLine(row, Number(input?.value || 0) + 1);
      });

      input?.addEventListener('change', () => {
        window.clearTimeout(quantityTimers.get(input));
        const nextQuantity = Math.max(0, Number(input.value || 0));
        quantityTimers.set(input, window.setTimeout(() => updateCartLine(row, nextQuantity), 280));
      });

      input?.addEventListener('input', () => {
        input.value = input.value.replace(/[^\d]/g, '');
      });

      input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        input.blur();
        updateCartLine(row, Math.max(0, Number(input.value || 0)));
      });
    });
  });

  document.querySelectorAll('[data-playtime-quick-add]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      const buttonText = form.querySelector('[data-playtime-button-text]');
      const section = form.closest('section');
      const status = section?.querySelector('[data-playtime-cart-status]');
      const originalText = buttonText?.textContent?.trim() || themeString('addToCart', 'Add to cart');
      const productTitle = form.getAttribute('data-product-title') || themeString('productFallback', 'Product');
      const viewCartUrl = status?.getAttribute('data-view-cart-url') || '/cart';
      const continueUrl = status?.getAttribute('data-continue-url') || '/collections/all';
      const card = form.closest('.playtime-product-card');

      if (!button || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      card?.classList.remove('is-error', 'is-added');
      card?.classList.add('is-loading');
      if (buttonText) buttonText.textContent = themeString('addingShort', 'Adding...');
      if (status) {
        status.classList.add('is-visible');
        status.classList.remove('is-error');
        status.textContent = themeString('adding', `Adding ${productTitle}...`, { __TITLE__: productTitle });
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || themeString('unableAdd', 'Unable to add product to cart.'));
        }

        button.classList.add('is-added');
        card?.classList.add('is-added');
        if (buttonText) buttonText.textContent = themeString('addedShort', 'Added');
        await updateCartCount(true);

        renderCartToast(status, {
          title: themeString('added', `${productTitle} added to cart.`, { __TITLE__: productTitle }),
          viewCartUrl,
          continueUrl
        });

        window.setTimeout(() => {
          button.classList.remove('is-added');
          card?.classList.remove('is-added');
          if (buttonText) buttonText.textContent = originalText;
          button.disabled = false;
        }, 1800);
      } catch (error) {
        button.classList.add('is-error');
        card?.classList.add('is-error');
        renderCartToast(status, {
          title: error.message,
          viewCartUrl,
          continueUrl,
          error: true
        });
        if (buttonText) buttonText.textContent = originalText;
        button.disabled = false;
        window.setTimeout(() => {
          button.classList.remove('is-error');
          card?.classList.remove('is-error');
        }, 2600);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        card?.classList.remove('is-loading');
      }
    });
  });

  document.querySelectorAll('[data-product-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      document.querySelectorAll('[data-product-variant-select]').forEach((otherSelect) => {
        if (otherSelect !== select) otherSelect.value = select.value;
      });
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const panels = gallery.querySelectorAll('[data-product-media-panel]');
    const thumbs = gallery.querySelectorAll('[data-product-media-thumb]');
    const mediaScroller = gallery.querySelector('.playtime-product__main-media');

    const activateMedia = (id, scroll = true) => {
      panels.forEach((panel) => {
        const active = panel.getAttribute('data-product-media-panel') === id;
        panel.classList.toggle('is-active', active);
        if (active && scroll && window.matchMedia('(max-width: 749px)').matches) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      });
      thumbs.forEach((item) => item.classList.toggle('is-active', item.getAttribute('data-product-media-thumb') === id));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const id = thumb.getAttribute('data-product-media-thumb');
        activateMedia(id);
      });
    });

    mediaScroller?.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const activePanel = [...panels].reduce((closest, panel) => {
        const distance = Math.abs(panel.getBoundingClientRect().left - mediaScroller.getBoundingClientRect().left);
        if (!closest || distance < closest.distance) return { panel, distance };
        return closest;
      }, null)?.panel;
      const id = activePanel?.getAttribute('data-product-media-panel');
      if (!id) return;
      thumbs.forEach((item) => item.classList.toggle('is-active', item.getAttribute('data-product-media-thumb') === id));
    }, { passive: true });
  });

  document.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
    const input = selector.querySelector('[data-quantity-input]');
    selector.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      input.value = Math.max(1, Number(input.value || 1) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    selector.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input?.addEventListener('input', () => {
      input.value = input.value.replace(/[^\d]/g, '');
    });
  });

  document.querySelectorAll('[data-sort-by]').forEach((select) => {
    select.addEventListener('change', () => {
      select.form?.submit();
    });
  });

  document.querySelectorAll('[data-playtime-search]').forEach((searchSection) => {
    const input = searchSection.querySelector('[data-search-input]');
    const modal = searchSection.querySelector('[data-search-modal]');
    const results = searchSection.querySelector('[data-search-modal-results]');
    const status = searchSection.querySelector('[data-search-modal-status]');
    const closeButtons = searchSection.querySelectorAll('[data-search-modal-close]');
    if (!input || !modal || !results || !status) return;

    let searchTimer = 0;
    let activeRequest = null;

    const openSearchModal = () => {
      modal.classList.add('is-visible');
      modal.setAttribute('aria-hidden', 'false');
    };

    const closeSearchModal = () => {
      modal.classList.remove('is-visible');
      modal.setAttribute('aria-hidden', 'true');
    };

    const createText = (tagName, text, className) => {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      element.textContent = text;
      return element;
    };

    const renderSearchFallback = (query) => {
      results.replaceChildren();
      const fallback = document.createElement('div');
      fallback.className = 'pt-search-modal__fallback';
      ['toy premium', 'lingerie', 'benessere intimo', 'giochi di coppia'].forEach((term) => {
        const link = document.createElement('a');
        link.href = `/search?q=${encodeURIComponent(term)}`;
        link.textContent = term;
        fallback.append(link);
      });
      if (query) {
        const allResults = document.createElement('a');
        allResults.href = `/search?q=${encodeURIComponent(query)}`;
        allResults.textContent = themeString('searchAllResults', 'See all results');
        fallback.prepend(allResults);
      }
      results.append(fallback);
    };

    const renderPredictiveResults = (payload, query) => {
      const predictiveResults = payload?.resources?.results || {};
      const products = predictiveResults.products || [];
      const collections = predictiveResults.collections || [];
      const pages = predictiveResults.pages || [];
      results.replaceChildren();

      if (!products.length && !collections.length && !pages.length) {
        status.textContent = themeString('searchNoSuggestions', 'No quick suggestions yet. Try one of the popular searches below.');
        renderSearchFallback(query);
        return;
      }

      status.textContent = themeString('searchSuggestions', 'Suggested results while you type.');

      products.slice(0, 5).forEach((product) => {
        const link = document.createElement('a');
        link.className = 'pt-search-modal__item';
        link.href = product.url || `/search?q=${encodeURIComponent(query)}`;

        const imageSource = typeof product.image === 'string'
          ? product.image
          : product.image?.url || product.featured_image?.url || product.featured_image || '';
        if (imageSource) {
          const image = document.createElement('img');
          image.className = 'pt-search-modal__image';
          image.alt = product.title || '';
          image.loading = 'lazy';
          image.src = imageSource;
          link.append(image);
        } else {
          link.classList.add('pt-search-modal__item--no-image');
        }

        const body = document.createElement('span');
        body.append(
          createText('small', themeString('searchProductLabel', 'Product')),
          createText('strong', product.title || themeString('productFallback', 'Product'))
        );
        link.append(body);

        if (product.price) {
          const price = document.createElement('em');
          price.textContent = product.price;
          link.append(price);
        }

        results.append(link);
      });

      [...collections.slice(0, 3), ...pages.slice(0, 2)].forEach((item) => {
        const link = document.createElement('a');
        link.className = 'pt-search-modal__item pt-search-modal__item--simple';
        link.href = item.url || `/search?q=${encodeURIComponent(query)}`;
        const body = document.createElement('span');
        body.append(
          createText('small', item.handle ? themeString('searchCollectionLabel', 'Collection') : themeString('searchPageLabel', 'Page')),
          createText('strong', item.title || query)
        );
        link.append(body);
        results.append(link);
      });
    };

    const fetchPredictiveResults = async (query) => {
      if (activeRequest) activeRequest.abort();
      activeRequest = new AbortController();
      status.textContent = themeString('searchLoading', 'Searching...');

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last`;
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: activeRequest.signal
        });
        if (!response.ok) throw new Error('Predictive search unavailable');
        renderPredictiveResults(await response.json(), query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        status.textContent = themeString('searchNoSuggestions', 'No quick suggestions yet. Try one of the popular searches below.');
        renderSearchFallback(query);
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(searchTimer);
      if (!query) {
        closeSearchModal();
        renderSearchFallback('');
        return;
      }
      openSearchModal();
      renderSearchFallback(query);
      searchTimer = window.setTimeout(() => fetchPredictiveResults(query), 260);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim()) openSearchModal();
    });

    closeButtons.forEach((button) => button.addEventListener('click', closeSearchModal));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-visible')) closeSearchModal();
    });
  });

  const normalizeProductHeader = async (section) => {
    const summary = section.querySelector('.playtime-product__summary');
    const title = summary?.querySelector('h1');
    if (!summary || !title || summary.querySelector('.playtime-product__topline')) return;

    const oldEyebrow = summary.querySelector('.playtime-product__eyebrow');
    const statusRow = summary.querySelector('.ptb-row');
    const topLine = document.createElement('div');
    const vendorBadge = document.createElement('a');

    topLine.className = 'playtime-product__topline';
    vendorBadge.className = 'playtime-product__vendor-badge';
    vendorBadge.hidden = true;
    topLine.append(vendorBadge);

    if (statusRow) topLine.append(statusRow);
    title.before(topLine);
    oldEyebrow?.remove();

    try {
      const response = await fetch(`${window.location.pathname}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product vendor unavailable');
      const productData = await response.json();
      const vendor = productData.vendor || '';
      if (!vendor) {
        vendorBadge.remove();
        return;
      }
      vendorBadge.textContent = vendor;
      vendorBadge.href = `/collections/vendors?q=${encodeURIComponent(vendor)}`;
      vendorBadge.hidden = false;
    } catch (error) {
      vendorBadge.remove();
    }
  };

  document.querySelectorAll('[data-playtime-product]').forEach((section) => {
    normalizeProductHeader(section);

    const variantsScript = section.querySelector('[data-product-json]');
    if (!variantsScript) return;

    const variants = JSON.parse(variantsScript.textContent || '[]');
    const optionInputs = section.querySelectorAll('[data-product-option]');
    const variantInput = section.querySelector('[data-product-variant-input]');
    const stickyVariantInput = section.querySelector('[data-sticky-variant-input]');
    const submitButton = section.querySelector('[data-product-submit]');
    const stickySubmit = section.querySelector('[data-sticky-submit]');
    const price = section.querySelector('[data-product-price]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const saleBadge = section.querySelector('[data-product-sale-badge]');
    const stickyAtc = section.querySelector('[data-mobile-sticky-atc]');
    const addLabel = section.getAttribute('data-add-label') || themeString('addToCart', 'Add to cart');
    const soldLabel = section.getAttribute('data-sold-label') || themeString('soldOut', 'Sold out');
    const mediaPanels = section.querySelectorAll('[data-product-media-panel]');
    const mediaThumbs = section.querySelectorAll('[data-product-media-thumb]');

    const updateStickyVisibility = () => {
      if (!stickyAtc) return;
      stickyAtc.classList.toggle('is-visible', window.scrollY > 420);
    };

    updateStickyVisibility();
    window.addEventListener('scroll', updateStickyVisibility, { passive: true });

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || '?' });
    };

    const selectedOptions = () => {
      const names = [...new Set([...optionInputs].map((input) => input.name))];
      return names.map((name) => section.querySelector(`[name="${CSS.escape(name)}"]:checked`)?.value);
    };

    const updateVariant = () => {
      const options = selectedOptions();
      const currentVariantId = Number(variantInput?.value || stickyVariantInput?.value || 0);
      const variant = options.length
        ? variants.find((item) => item.options.every((option, index) => option === options[index]))
        : variants.find((item) => Number(item.id) === currentVariantId) || variants[0];
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;
      if (stickyVariantInput) stickyVariantInput.value = variant.id;

      const priceHtml = `${variant.compare_at_price && variant.compare_at_price > variant.price ? `<s>${formatMoney(variant.compare_at_price)}</s>` : ''}<strong>${formatMoney(variant.price)}</strong>`;
      const stickyPriceHtml = `${variant.compare_at_price && variant.compare_at_price > variant.price ? `<s>${formatMoney(variant.compare_at_price)}</s>` : ''}${formatMoney(variant.price)}`;
      if (price) price.innerHTML = priceHtml;
      if (stickyPrice) stickyPrice.innerHTML = stickyPriceHtml;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (saleBadge) {
        saleBadge.classList.toggle('is-hidden', !onSale);
        if (onSale) {
          const savings = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          saleBadge.textContent = themeString('savePercent', `Save ${savings}%`, { __PERCENT__: String(savings) });
        }
      }

      [submitButton, stickySubmit].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available ? addLabel : soldLabel;
      });

      if (variant.featured_media && variant.featured_media.id) {
        const mediaId = String(variant.featured_media.id);
        mediaPanels.forEach((panel) => panel.classList.toggle('is-active', panel.getAttribute('data-product-media-panel') === mediaId));
        mediaThumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.getAttribute('data-product-media-thumb') === mediaId));
        const activePanel = section.querySelector(`[data-product-media-panel="${CSS.escape(mediaId)}"]`);
        if (activePanel && window.matchMedia('(max-width: 749px)').matches) {
          activePanel.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      }
    };

    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
    updateVariant();
  });
})();

(() => {
  const initArchedHeroSliders = (root = document) => {
    const sliders = root.querySelectorAll('[data-arched-hero-slider]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    sliders.forEach((slider) => {
      if (slider.dataset.archedHeroReady === 'true') return;
      slider.dataset.archedHeroReady = 'true';

      const slides = [...slider.querySelectorAll('[data-arched-hero-slide]')];
      const dots = [...slider.querySelectorAll('[data-arched-hero-dot]')];
      if (slides.length <= 1) return;

      let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      if (current < 0) current = 0;

      const activate = (next) => {
        if (next === current) return;

        const previousSlide = slides[current];
        const nextSlide = slides[next];

        previousSlide.classList.remove('is-active', 'is-entering');
        previousSlide.classList.add('is-exiting');
        nextSlide.classList.add('is-active', 'is-entering');

        dots[current]?.classList.remove('is-active');
        dots[next]?.classList.add('is-active');

        window.setTimeout(() => {
          previousSlide.classList.remove('is-exiting');
          nextSlide.classList.remove('is-entering');
        }, 820);

        current = next;
      };

      if (!reduceMotion) {
        window.setInterval(() => {
          activate((current + 1) % slides.length);
        }, 2000);
      }
    });
  };

  document.addEventListener('DOMContentLoaded', () => initArchedHeroSliders());
  document.addEventListener('shopify:section:load', (event) => initArchedHeroSliders(event.target));
})();

(() => {
  const initCollectionFilters = (root = document) => {
    root.querySelectorAll('[data-collection-filters]').forEach((filters) => {
      if (filters.dataset.filtersReady === 'true') return;
      filters.dataset.filtersReady = 'true';

      const toggle = filters.querySelector('[data-collection-filters-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', () => {
        const isOpen = filters.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => initCollectionFilters());
  document.addEventListener('shopify:section:load', (event) => initCollectionFilters(event.target));
})();
